import { ArrowRight, BookOpen, Clock, Flame, Award, TrendingUp, User } from "lucide-react";
import { Link } from "react-router-dom";
import type { Article } from "../types";
import { formatDate } from "../utils/date";

type MagazineHeroProps = {
  primaryArticle?: Article | null;
  sidebarArticles?: Article[];
  editionBadge?: string;
};

export function MagazineHero({
  primaryArticle,
  sidebarArticles = [],
  editionBadge = "DAILY EDITION"
}: MagazineHeroProps) {
  if (!primaryArticle) return null;

  return (
    <section className="mag-hero-section" aria-label="Cover Stories and Lead Dispatches">
      {/* Section Header with Issue Badge */}
      <div className="mag-hero-top-bar">
        <div className="mag-hero-label-wrap">
          <span className="mag-hero-pulse-dot" />
          <h2 className="mag-hero-section-heading">COVER STORY & LEAD DISPATCHES</h2>
        </div>
        <div className="mag-hero-edition-tag">
          <Award size={13} className="text-amber-600" />
          <span>{editionBadge}</span>
        </div>
      </div>

      <div className="mag-hero-grid">
        {/* Main Cover Story (Left / Dominant Column) */}
        <article className="mag-hero-primary-card">
          <div className="mag-hero-visual-wrap">
            <Link
              to={`/article/${primaryArticle.slug}`}
              className="mag-hero-img-link"
              tabIndex={-1}
              aria-hidden="true"
            >
              {primaryArticle.image?.url ? (
                <img
                  src={primaryArticle.image.url}
                  alt={primaryArticle.image.alt_text || primaryArticle.title}
                  className="mag-hero-img"
                  loading="eager"
                />
              ) : (
                <div className="mag-hero-img-placeholder">
                  <BookOpen size={48} className="text-amber-800/40" />
                </div>
              )}
              <div className="mag-hero-img-overlay" />
              <div className="mag-hero-img-badge">
                <span className="badge-text">COVER STORY</span>
              </div>
            </Link>

            {primaryArticle.image?.credit && (
              <div className="mag-hero-credit">
                <span>Photo: {primaryArticle.image.credit}</span>
              </div>
            )}
          </div>

          <div className="mag-hero-content">
            <div className="mag-hero-meta-row">
              <Link
                to={`/category/${primaryArticle.category.slug}`}
                className={`mag-hero-category-kicker mag-cat-kicker-${primaryArticle.category.slug}`}
              >
                <span className="mag-kicker-dot" />
                <span>{primaryArticle.category.name}</span>
              </Link>
              {primaryArticle.disclosure_label && primaryArticle.disclosure_label !== "editorial" && (
                <span className="mag-hero-disclosure-tag">
                  {primaryArticle.disclosure_label.replace("_", " ")}
                </span>
              )}
              <span className="mag-hero-meta-divider">•</span>
              <span className="mag-hero-reading-time">
                <Clock size={13} />
                <span>{primaryArticle.reading_time} min read</span>
              </span>
            </div>

            <h1 className="mag-hero-title">
              <Link to={`/article/${primaryArticle.slug}`} className="mag-hero-title-link">
                {primaryArticle.title}
              </Link>
            </h1>

            {primaryArticle.subtitle && (
              <p className="mag-hero-subtitle">{primaryArticle.subtitle}</p>
            )}

            {primaryArticle.excerpt && (
              <p className="mag-hero-excerpt">{primaryArticle.excerpt}</p>
            )}

            <div className="mag-hero-footer">
              <div className="mag-hero-author">
                <div className="mag-hero-author-avatar">
                  <User size={16} />
                </div>
                <div className="mag-hero-author-info">
                  <span className="by-text">By</span>{" "}
                  <Link
                    to={`/author/${primaryArticle.author.slug}`}
                    className="mag-hero-author-name"
                  >
                    {primaryArticle.author.name}
                  </Link>
                  <span className="date-sep">•</span>
                  <span className="mag-hero-date">{formatDate(primaryArticle.published_at)}</span>
                </div>
              </div>

              <Link
                to={`/article/${primaryArticle.slug}`}
                className="mag-hero-cta-btn"
                aria-label={`Read full story: ${primaryArticle.title}`}
              >
                <span>Read Cover Story</span>
                <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </article>

        {/* Supporting Front Page Dispatches Sidebar (Right Column) */}
        <aside className="mag-hero-sidebar" aria-label="Front Page Selections">
          <div className="mag-hero-sidebar-header">
            <div className="flex items-center gap-2">
              <Flame size={16} className="text-amber-600" />
              <h3 className="mag-hero-sidebar-title">FRONT PAGE SELECTIONS</h3>
            </div>
            <span className="mag-hero-sidebar-count">{sidebarArticles.length} STORIES</span>
          </div>

          <div className="mag-hero-sidebar-list">
            {sidebarArticles.map((story, index) => (
              <article
                key={story.id}
                className={`mag-hero-sidebar-item mag-cat-item-${story.category.slug}`}
              >
                <div className={`mag-hero-rank-num mag-rank-${index + 2}`}>
                  <span>{String(index + 2).padStart(2, "0")}</span>
                </div>

                <div className="mag-hero-sidebar-item-content">
                  <div className="mag-hero-sidebar-kicker-row">
                    <Link
                      to={`/category/${story.category.slug}`}
                      className={`mag-hero-sidebar-kicker mag-cat-kicker-${story.category.slug}`}
                    >
                      <span className="mag-kicker-dot" />
                      <span>{story.category.name}</span>
                    </Link>
                    <span className="kicker-dot">•</span>
                    <span className="mag-hero-sidebar-time">{story.reading_time}m</span>
                  </div>

                  <h4 className="mag-hero-sidebar-heading">
                    <Link to={`/article/${story.slug}`} className="mag-hero-sidebar-link">
                      {story.title}
                    </Link>
                  </h4>

                  {story.subtitle && (
                    <p className="mag-hero-sidebar-dek">{story.subtitle}</p>
                  )}

                  <div className="mag-hero-sidebar-author-row">
                    <span className="author-label">By {story.author.name}</span>
                    <span className="author-dot">•</span>
                    <span>{formatDate(story.published_at)}</span>
                  </div>
                </div>

                {story.image?.url && (
                  <Link
                    to={`/article/${story.slug}`}
                    className="mag-hero-sidebar-thumb-link"
                    tabIndex={-1}
                    aria-hidden="true"
                  >
                    <img
                      src={story.image.url}
                      alt={story.image.alt_text || story.title}
                      className="mag-hero-sidebar-thumb"
                      loading="lazy"
                    />
                  </Link>
                )}
              </article>
            ))}
          </div>
        </aside>
      </div>
    </section>
  );
}
